import { api } from './axios';

const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * Типы сущностей для структурированного хранения файлов
 * @enum {string}
 */
export const EntityType = {
    PROFILE: 'PROFILE',
    BATTLE: 'BATTLE',
    DUEL: 'DUEL',
    CHAT: 'CHAT'
};

/**
 * Строит query параметры для загрузки файлов с entityType и entityId
 * @param {string} [entityType] - Тип сущности
 * @param {string} [entityId] - ID сущности
 * @returns {Object} Параметры для axios
 */
const buildEntityParams = (entityType, entityId) => {
    const params = {};
    if (entityType) params.entityType = entityType;
    if (entityId) params.entityId = entityId;
    return params;
};

/**
 * Кэш Blob URL для предотвращения повторных запросов
 * Map<fileId, { url: string, timestamp: number, blob: Blob }>
 */
const blobCache = new Map();

/**
 * Время жизни кэша в мс (5 минут)
 */
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Очищает устаревшие записи из кэша
 */
const cleanupCache = () => {
    const now = Date.now();
    for (const [key, value] of blobCache.entries()) {
        if (now - value.timestamp > CACHE_TTL) {
            if (value.url) {
                window.URL.revokeObjectURL(value.url);
            }
            blobCache.delete(key);
        }
    }
};

/**
 * Создаёт Blob URL из blob с кэшированием
 * @param {string} fileId - ID файла для кэша
 * @param {Blob} blob - Blob данные
 * @returns {string} URL для использования в <img>, <a>, <video>, <audio>
 */
export const createBlobUrl = (fileId, blob) => {
    // Очищаем старые записи периодически
    if (blobCache.size > 50) {
        cleanupCache();
    }

    // Если уже есть в кэше — возвращаем существующий URL
    if (blobCache.has(fileId)) {
        const cached = blobCache.get(fileId);
        if (Date.now() - cached.timestamp < CACHE_TTL) {
            return cached.url;
        }
        // Истекает срок — перевыпускаем URL
        if (cached.url) {
            window.URL.revokeObjectURL(cached.url);
        }
    }

    const url = window.URL.createObjectURL(blob);
    blobCache.set(fileId, { url, timestamp: Date.now(), blob });

    // Автоматическая очистка через 5 минут
    setTimeout(() => {
        const cached = blobCache.get(fileId);
        if (cached && cached.url) {
            window.URL.revokeObjectURL(cached.url);
        }
        blobCache.delete(fileId);
    }, CACHE_TTL);

    return url;
};

/**
 * Скачивает файл как blob и создаёт URL для отображения
 * @param {Function} downloadFn - Функция скачивания (downloadImage, downloadAudio, etc.)
 * @param {string|number} id - ID файла
 * @param {Object} [options] - Опции
 * @param {number} [options.retries=3] - Количество попыток при 429 ошибке
 * @param {number} [options.retryDelay=1000] - Задержка между попытками (мс)
 * @returns {Promise<string>} Blob URL
 */
export const getFileBlobUrl = async (downloadFn, id, options = {}) => {
    const { retries = 3, retryDelay = 1000 } = options;

    // Проверяем кэш
    if (blobCache.has(id)) {
        const cached = blobCache.get(id);
        if (Date.now() - cached.timestamp < CACHE_TTL) {
            return cached.url;
        }
    }

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const blob = await downloadFn(id);
            return createBlobUrl(id, blob);
        } catch (error) {
            lastError = error;

            // Если это 429 ошибка — ждём и пробуем снова
            if (error.response?.status === 429) {
                const retryAfter = error.response?.headers?.['retry-after']
                    ? parseInt(error.response.headers['retry-after']) * 1000
                    : retryDelay * (attempt + 1); // Exponential backoff

                console.warn(`[filesApi] 429 error, retrying in ${retryAfter}ms (attempt ${attempt + 1}/${retries})`);
                await new Promise(resolve => setTimeout(resolve, retryAfter));
                continue;
            }

            // Другие ошибки — сразу выбрасываем
            throw error;
        }
    }

    throw lastError;
};

/**
 * Получает файл из кэша или скачивает заново
 * @param {string} fileId - ID файла
 * @returns {{ url: string, blob: Blob } | null}
 */
export const getCachedBlobUrl = (fileId) => {
    const cached = blobCache.get(fileId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return { url: cached.url, blob: cached.blob };
    }
    return null;
};

/**
 * Очищает кэш blob URL
 */
export const clearBlobCache = () => {
    for (const [, value] of blobCache.entries()) {
        if (value.url) {
            window.URL.revokeObjectURL(value.url);
        }
    }
    blobCache.clear();
};

/**
 * Инициирует скачивание файла через создание временной ссылки
 * @param {Blob} blob - Blob данные
 * @param {string} filename - Имя файла для сохранения
 */
export const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export const filesApi = {
    /**
     * Получить список файлов
     * @param {Object} params - Параметры пагинации (page, size)
     * @returns {Promise<Object>}
     */
    list: async (params = {}) => {
        try {
            const { data } = await api.get('/files', { params });
            return data;
        } catch (error) {
            console.error('[filesApi.list] Failed to fetch files:', error.message);
            throw error;
        }
    },

    /**
     * Загружает файл (max 30MB)
     * @param {File} file - Файл для загрузки
     * @param {Object} [metadata={}] - Метаданные файла (type, extra info)
     * @param {Function} [onUploadProgress] - Коллбэк для отслеживания прогресса
     * @returns {Promise<Object>}
     */
    upload: async (file, metadata = {}, onUploadProgress = null) => {
        if (!file) throw new Error('File is required');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('metadata', JSON.stringify(metadata));

        try {
            const { data } = await api.post('/files/upload', formData, {
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error('[filesApi.upload] Failed to upload file:', error.message);
            throw error;
        }
    },

    /**
     * Загружает изображение с поддержкой entityType/entityId
     * Endpoint: POST /api/files/upload/image
     * @param {File} file - Файл изображения
     * @param {Object} [options] - Опции загрузки
     * @param {string} [options.entityType] - Тип сущности (PROFILE, BATTLE, DUEL, CHAT)
     * @param {string} [options.entityId] - ID сущности
     * @param {Function} [options.onUploadProgress] - Коллбэк прогресса (progressEvent)
     * @returns {Promise<Object>} Данные файла с thumbnailUrl
     */
    uploadImage: async (file, options = {}) => {
        if (!file) throw new Error('File is required');
        const { entityType, entityId, onUploadProgress } = options;
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await api.post('/files/upload/image', formData, {
                params: buildEntityParams(entityType, entityId),
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error('[filesApi.uploadImage] Failed to upload image:', error.message);
            throw error;
        }
    },

    /**
     * Загружает аудиофайл с поддержкой entityType/entityId
     * Endpoint: POST /api/files/upload/audio
     * @param {File} file - Аудиофайл
     * @param {Object} [options] - Опции загрузки
     * @param {string} [options.entityType] - Тип сущности
     * @param {string} [options.entityId] - ID сущности
     * @param {Function} [options.onUploadProgress] - Коллбэк прогресса
     * @returns {Promise<Object>}
     */
    uploadAudio: async (file, options = {}) => {
        if (!file) throw new Error('File is required');
        const { entityType, entityId, onUploadProgress } = options;
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await api.post('/files/upload/audio', formData, {
                params: buildEntityParams(entityType, entityId),
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error('[filesApi.uploadAudio] Failed to upload audio:', error.message);
            throw error;
        }
    },

    /**
     * Загружает видеофайл с поддержкой entityType/entityId
     * Endpoint: POST /api/files/upload/video
     * @param {File} file - Видеофайл
     * @param {Object} [options] - Опции загрузки
     * @param {string} [options.entityType] - Тип сущности
     * @param {string} [options.entityId] - ID сущности
     * @param {Function} [options.onUploadProgress] - Коллбэк прогресса
     * @returns {Promise<Object>}
     */
    uploadVideo: async (file, options = {}) => {
        if (!file) throw new Error('File is required');
        const { entityType, entityId, onUploadProgress } = options;
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await api.post('/files/upload/video', formData, {
                params: buildEntityParams(entityType, entityId),
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error('[filesApi.uploadVideo] Failed to upload video:', error.message);
            throw error;
        }
    },

    /**
     * Загружает аватар профиля
     * Endpoint: POST /api/profiles/{id}/avatar
     * @param {string|number} userId - ID пользователя
     * @param {File} file - Файл изображения
     * @param {Function} [onUploadProgress] - Коллбэк прогресса
     * @returns {Promise<Object>}
     */
    uploadProfileAvatar: async (userId, file, onUploadProgress = null) => {
        validateId(userId, 'User ID');
        if (!file) throw new Error('File is required');
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await api.post(`/profiles/${userId}/avatar`, formData, {
                params: { entityType: 'PROFILE', entityId: userId },
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error('[filesApi.uploadProfileAvatar] Failed to upload avatar:', error.message);
            throw error;
        }
    },

    /**
     * Загружает обложку профиля
     * Endpoint: POST /api/profiles/{id}/cover
     * @param {string|number} userId - ID пользователя
     * @param {File} file - Файл изображения
     * @param {Function} [onUploadProgress] - Коллбэк прогресса
     * @returns {Promise<Object>}
     */
    uploadProfileCover: async (userId, file, onUploadProgress = null) => {
        validateId(userId, 'User ID');
        if (!file) throw new Error('File is required');
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await api.post(`/profiles/${userId}/cover`, formData, {
                params: { entityType: 'PROFILE', entityId: userId },
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error('[filesApi.uploadProfileCover] Failed to upload cover:', error.message);
            throw error;
        }
    },

    /**
     * Загружает обложку баттла
     * Endpoint: POST /api/battles/{id}/cover
     * @param {string|number} battleId - ID баттла
     * @param {File} file - Файл изображения
     * @param {Function} [onUploadProgress] - Коллбэк прогресса
     * @returns {Promise<Object>}
     */
    uploadBattleCover: async (battleId, file, onUploadProgress = null) => {
        validateId(battleId, 'Battle ID');
        if (!file) throw new Error('File is required');
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await api.post(`/battles/${battleId}/cover`, formData, {
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error('[filesApi.uploadBattleCover] Failed to upload battle cover:', error.message);
            throw error;
        }
    },

    /**
     * Загружает обложку дуэли
     * Endpoint: POST /api/duels/{id}/cover
     * @param {string|number} duelId - ID дуэли
     * @param {File} file - Файл изображения
     * @param {Function} [onUploadProgress] - Коллбэк прогресса
     * @returns {Promise<Object>}
     */
    uploadDuelCover: async (duelId, file, onUploadProgress = null) => {
        validateId(duelId, 'Duel ID');
        if (!file) throw new Error('File is required');
        const formData = new FormData();
        formData.append('file', file);

        try {
            const { data } = await api.post(`/duels/${duelId}/cover`, formData, {
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error('[filesApi.uploadDuelCover] Failed to upload duel cover:', error.message);
            throw error;
        }
    },

    /**
     * Получает информацию о файле
     * @param {string|number} id - ID файла
     * @returns {Promise<Object>}
     */
    get: async (id) => {
        validateId(id, 'File ID');
        try {
            const { data } = await api.get(`/files/${id}`);
            return data;
        } catch (error) {
            console.error(`[filesApi.get] Failed to fetch file ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет файл
     * @param {string|number} id - ID файла
     * @returns {Promise<Object>}
     */
    remove: async (id) => {
        validateId(id, 'File ID');
        try {
            const { data } = await api.delete(`/files/${id}`);
            return data;
        } catch (error) {
            console.error(`[filesApi.remove] Failed to delete file ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Скачивает файл (универсальный метод)
     * Endpoint: GET /api/files/{id}/download
     * @param {string|number} id - ID файла
     * @returns {Promise<Blob>}
     */
    download: async (id) => {
        validateId(id, 'File ID');
        try {
            const { data } = await api.get(`/files/${id}/download`, { responseType: 'blob' });
            return data;
        } catch (error) {
            console.error(`[filesApi.download] Failed to download file ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Скачивает изображение по ID с retry logic для 429 ошибок
     * Endpoint: GET /api/files/download/image/{id}
     * @param {string|number} id - ID файла
     * @param {Object} [options] - Опции
     * @param {number} [options.retries=3] - Количество попыток при 429
     * @param {number} [options.retryDelay=1000] - Задержка между попытками (мс)
     * @returns {Promise<Blob>}
     */
    downloadImage: async (id, options = {}) => {
        validateId(id, 'Image ID');
        const { retries = 3, retryDelay = 1000 } = options;

        let lastError;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                const response = await api.get(`/files/download/image/${id}`, {
                    responseType: 'blob'
                });
                return response.data;
            } catch (error) {
                lastError = error;

                // 429 — rate limit, пробуем снова с задержкой
                if (error.response?.status === 429) {
                    const retryAfter = error.response?.headers?.['retry-after']
                        ? parseInt(error.response.headers['retry-after']) * 1000
                        : retryDelay * (attempt + 1);

                    if (attempt < retries) {
                        console.warn(`[filesApi.downloadImage] 429 error, retrying in ${retryAfter}ms (attempt ${attempt + 1}/${retries})`);
                        await new Promise(resolve => setTimeout(resolve, retryAfter));
                        continue;
                    }
                }

                // Логируем ошибку
                if (error.response?.data instanceof Blob) {
                    try {
                        const errorText = await error.response.data.text();
                        const errorJson = JSON.parse(errorText);
                        console.error(`[filesApi.downloadImage] Server error:`, errorJson);
                    } catch (e) {
                        // Игнорируем ошибку парсинга
                    }
                }
                console.error(`[filesApi.downloadImage] Failed to download image ${id}:`, error.message);
                break;
            }
        }

        throw lastError;
    },

    /**
     * Скачивает аудиофайл по ID с retry logic для 429 ошибок
     * Endpoint: GET /api/files/download/audio/{id}
     * @param {string|number} id - ID файла
     * @param {Object} [options] - Опции
     * @param {number} [options.retries=3] - Количество попыток при 429
     * @param {number} [options.retryDelay=1000] - Задержка между попытками (мс)
     * @returns {Promise<Blob>}
     */
    downloadAudio: async (id, options = {}) => {
        validateId(id, 'Audio ID');
        const { retries = 3, retryDelay = 1000 } = options;

        let lastError;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                const response = await api.get(`/files/download/audio/${id}`, {
                    responseType: 'blob'
                });
                return response.data;
            } catch (error) {
                lastError = error;

                if (error.response?.status === 429) {
                    const retryAfter = error.response?.headers?.['retry-after']
                        ? parseInt(error.response.headers['retry-after']) * 1000
                        : retryDelay * (attempt + 1);

                    if (attempt < retries) {
                        console.warn(`[filesApi.downloadAudio] 429 error, retrying in ${retryAfter}ms (attempt ${attempt + 1}/${retries})`);
                        await new Promise(resolve => setTimeout(resolve, retryAfter));
                        continue;
                    }
                }

                if (error.response?.data instanceof Blob) {
                    try {
                        const errorText = await error.response.data.text();
                        const errorJson = JSON.parse(errorText);
                        console.error(`[filesApi.downloadAudio] Server error:`, errorJson);
                    } catch (e) {
                        // Игнорируем ошибку парсинга
                    }
                }
                console.error(`[filesApi.downloadAudio] Failed to download audio ${id}:`, error.message);
                break;
            }
        }

        throw lastError;
    },

    /**
     * Скачивает видеофайл по ID с retry logic для 429 ошибок
     * Endpoint: GET /api/files/download/video/{id}
     * @param {string|number} id - ID файла
     * @param {Object} [options] - Опции
     * @param {number} [options.retries=3] - Количество попыток при 429
     * @param {number} [options.retryDelay=1000] - Задержка между попытками (мс)
     * @returns {Promise<Blob>}
     */
    downloadVideo: async (id, options = {}) => {
        validateId(id, 'Video ID');
        const { retries = 3, retryDelay = 1000 } = options;

        let lastError;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                const response = await api.get(`/files/download/video/${id}`, {
                    responseType: 'blob'
                });
                return response.data;
            } catch (error) {
                lastError = error;

                if (error.response?.status === 429) {
                    const retryAfter = error.response?.headers?.['retry-after']
                        ? parseInt(error.response.headers['retry-after']) * 1000
                        : retryDelay * (attempt + 1);

                    if (attempt < retries) {
                        console.warn(`[filesApi.downloadVideo] 429 error, retrying in ${retryAfter}ms (attempt ${attempt + 1}/${retries})`);
                        await new Promise(resolve => setTimeout(resolve, retryAfter));
                        continue;
                    }
                }

                if (error.response?.data instanceof Blob) {
                    try {
                        const errorText = await error.response.data.text();
                        const errorJson = JSON.parse(errorText);
                        console.error(`[filesApi.downloadVideo] Server error:`, errorJson);
                    } catch (e) {
                        // Игнорируем ошибку парсинга
                    }
                }
                console.error(`[filesApi.downloadVideo] Failed to download video ${id}:`, error.message);
                break;
            }
        }

        throw lastError;
    },

    /**
     * Получает thumbnail изображения
     * Endpoint: GET /api/files/thumbnails/{filename}
     * @param {string} filename - Имя файла thumbnail (например, avatar_thumb_150px.jpg)
     * @returns {Promise<Blob>}
     */
    getThumbnail: async (filename) => {
        if (!filename) throw new Error('Filename is required');
        try {
            const response = await api.get(`/files/thumbnails/${filename}`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            console.error(`[filesApi.getThumbnail] Failed to fetch thumbnail ${filename}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает URL thumbnail для отображения в <img>
     * @param {string} filename - Имя файла thumbnail
     * @returns {Promise<string>} Blob URL
     */
    getThumbnailUrl: async (filename) => {
        const blob = await this.getThumbnail(filename);
        return createBlobUrl(blob);
    },

    /**
     * Скачивает файл с указанием имени
     * @param {string|number} id - ID файла
     * @param {string} filename - Имя файла для сохранения
     */
    downloadFile: async (id, filename) => {
        validateId(id, 'File ID');
        try {
            const blob = await this.download(id);
            triggerDownload(blob, filename);
        } catch (error) {
            console.error(`[filesApi.downloadFile] Failed to download file ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает прямую ссылку на файл для вставки в src
     * Endpoint: GET /api/files/{id}/download
     * @param {string|number} id - ID файла
     * @returns {string} URL для использования в <img src="...">, <video src="...">, etc.
     */
    getFileUrl: (id) => {
        validateId(id, 'File ID');
        return `${api.defaults.baseURL}/files/${id}/download`;
    },

    /**
     * Получает прямую ссылку на thumbnail
     * Endpoint: GET /api/files/thumbnails/{filename}
     * @param {string} filename - Имя файла thumbnail
     * @returns {string} URL для использования в <img src="...">
     */
    getThumbnailUrlStatic: (filename) => {
        if (!filename) throw new Error('Filename is required');
        return `${api.defaults.baseURL}/files/thumbnails/${filename}`;
    },

    /**
     * Пакетная загрузка файлов (макс. 10 файлов)
     * Endpoint: POST /api/files/upload/batch
     * @param {File[]} files - Массив файлов для загрузки
     * @param {Object} [options] - Опции
     * @param {string} [options.entityType] - Тип сущности
     * @param {string} [options.entityId] - ID сущности
     * @param {Function} [options.onUploadProgress] - Коллбэк прогресса
     * @returns {Promise<{message: string, files: Array, uploadedCount: number, duplicateCount: number, failedCount: number, errors: Array}>}
     */
    uploadBatch: async (files, options = {}) => {
        if (!Array.isArray(files) || files.length === 0) {
            throw new Error('files array is required');
        }
        if (files.length > 10) {
            throw new Error('Maximum 10 files allowed for batch upload');
        }

        const { entityType, entityId, onUploadProgress } = options;
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        if (entityType) formData.append('entityType', entityType);
        if (entityId) formData.append('entityId', entityId);

        try {
            const { data } = await api.post('/files/upload/batch', formData, {
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error('[filesApi.uploadBatch] Failed to batch upload files:', error.message);
            throw error;
        }
    },

    /**
     * Обновление метаданных файла
     * Endpoint: PUT /api/files/{id}/metadata
     * @param {string|number} id - ID файла
     * @param {Object} metadata - Метаданные { fileId, description, tags, isPublic, entityType, entityId }
     * @returns {Promise<Object>}
     */
    updateMetadata: async (id, metadata) => {
        validateId(id, 'File ID');
        if (!metadata) throw new Error('Metadata is required');
        try {
            const { data } = await api.put(`/files/${id}/metadata`, { ...metadata, fileId: id });
            return data;
        } catch (error) {
            console.error(`[filesApi.updateMetadata] Failed to update metadata for file ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновление информации о файле
     * Endpoint: PUT /api/files/{id}
     * @param {string|number} id - ID файла
     * @param {Object} payload - { fileName, description, isPublic }
     * @returns {Promise<Object>}
     */
    update: async (id, payload) => {
        validateId(id, 'File ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/files/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[filesApi.update] Failed to update file ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Поиск файлов по метаданным
     * Endpoint: GET /api/files/search
     * @param {Object} params - Параметры поиска { userId, description, tag, tags, isPublic, fileType, entityType, entityId, minFileSize, maxFileSize, dateFrom, dateTo, sortBy, sortDir, page, size }
     * @returns {Promise<{content: Array, page: number, size: number, totalElements: number, totalPages: number, hasNext: boolean, hasPrevious: boolean}>}
     */
    search: async (params = {}) => {
        try {
            const { data } = await api.get('/files/search', { params });
            return data;
        } catch (error) {
            console.error('[filesApi.search] Failed to search files:', error.message);
            throw error;
        }
    },

    /**
     * Поиск файлов по тегу
     * Endpoint: GET /api/files/search/by-tag
     * @param {string} tag - Тег для поиска
     * @returns {Promise<Array>}
     */
    searchByTag: async (tag) => {
        if (!tag) throw new Error('Tag is required');
        try {
            const { data } = await api.get('/files/search/by-tag', { params: { tag } });
            return data;
        } catch (error) {
            console.error('[filesApi.searchByTag] Failed to search files by tag:', error.message);
            throw error;
        }
    },

    /**
     * Получение файлов пользователя
     * Endpoint: GET /api/files/user/{userId}
     * @param {string|number} userId - ID пользователя
     * @returns {Promise<Array>}
     */
    getUserFiles: async (userId) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/files/user/${userId}`);
            return data;
        } catch (error) {
            console.error(`[filesApi.getUserFiles] Failed to fetch files for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получение файлов пользователя с метаданными
     * Endpoint: GET /api/files/user/{userId}/with-metadata
     * @param {string|number} userId - ID пользователя
     * @returns {Promise<Array>}
     */
    getUserFilesWithMetadata: async (userId) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/files/user/${userId}/with-metadata`);
            return data;
        } catch (error) {
            console.error(`[filesApi.getUserFilesWithMetadata] Failed to fetch files with metadata for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получение публичных файлов пользователя
     * Endpoint: GET /api/files/user/{userId}/public
     * @param {string|number} userId - ID пользователя
     * @returns {Promise<Array>}
     */
    getUserPublicFiles: async (userId) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/files/user/${userId}/public`);
            return data;
        } catch (error) {
            console.error(`[filesApi.getUserPublicFiles] Failed to fetch public files for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получение квот пользователя
     * Endpoint: GET /api/files/quota
     * @returns {Promise<{userId: string, totalFiles: number, maxFiles: number, imageFiles: number, maxImageFiles: number, audioFiles: number, maxAudioFiles: number, videoFiles: number, maxVideoFiles: number, totalSizeBytes: number, maxTotalSizeBytes: number, totalSizeFormatted: string, maxTotalSizeFormatted: string, usagePercent: number}>}
     */
    getQuota: async () => {
        try {
            const { data } = await api.get('/files/quota');
            return data;
        } catch (error) {
            console.error('[filesApi.getQuota] Failed to fetch quota:', error.message);
            throw error;
        }
    },

    /**
     * Получение квот указанного пользователя (ADMIN)
     * Endpoint: GET /api/files/user/{userId}/quota
     * @param {string|number} userId - ID пользователя
     * @returns {Promise<Object>}
     */
    getUserQuota: async (userId) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/files/user/${userId}/quota`);
            return data;
        } catch (error) {
            console.error(`[filesApi.getUserQuota] Failed to fetch quota for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Предпросмотр очистки неиспользуемых файлов (ADMIN)
     * Endpoint: GET /api/files/admin/cleanup/preview
     * @param {Object} params - Параметры { daysThreshold }
     * @returns {Promise<{message: string, filesToDelete: Array, totalFilesToDelete: number, totalSizeToDelete: number, totalSizeFormatted: string, dryRun: true}>}
     */
    getCleanupPreview: async (params = {}) => {
        try {
            const { data } = await api.get('/files/admin/cleanup/preview', { params });
            return data;
        } catch (error) {
            console.error('[filesApi.getCleanupPreview] Failed to fetch cleanup preview:', error.message);
            throw error;
        }
    },

    /**
     * Выполнение очистки неиспользуемых файлов (ADMIN)
     * Endpoint: POST /api/files/admin/cleanup
     * @param {Object} payload - { daysThreshold, dryRun: false }
     * @returns {Promise<{message: string, filesToDelete: Array, deletedCount: number, deletedSize: number, deletedSizeFormatted: string, dryRun: false}>}
     */
    executeCleanup: async (payload = {}) => {
        try {
            const { data } = await api.post('/files/admin/cleanup', { ...payload, dryRun: false });
            return data;
        } catch (error) {
            console.error('[filesApi.executeCleanup] Failed to execute cleanup:', error.message);
            throw error;
        }
    },

    /**
     * Получение версий файла
     * Endpoint: GET /api/files/versions/{fileId}
     * @param {string|number} fileId - ID файла
     * @returns {Promise<Array>}
     */
    getFileVersions: async (fileId) => {
        validateId(fileId, 'File ID');
        try {
            const { data } = await api.get(`/files/versions/${fileId}`);
            return data;
        } catch (error) {
            console.error(`[filesApi.getFileVersions] Failed to fetch versions for file ${fileId}:`, error.message);
            throw error;
        }
    },

    /**
     * Поиск файла по названию
     * Endpoint: GET /api/files/name/{name}
     * @param {string} name - Название файла
     * @returns {Promise<Object|null>}
     */
    getByName: async (name) => {
        if (!name) throw new Error('File name is required');
        try {
            const { data } = await api.get(`/files/name/${encodeURIComponent(name)}`);
            return data;
        } catch (error) {
            console.error(`[filesApi.getByName] Failed to fetch file by name ${name}:`, error.message);
            throw error;
        }
    },

    /**
     * Поиск публичных файлов
     * Endpoint: GET /api/files/public/search
     * @param {Object} params - Параметры пагинации { page, size }
     * @returns {Promise<{content: Array, page: number, size: number, totalElements: number, totalPages: number, hasNext: boolean, hasPrevious: boolean}>}
     */
    searchPublic: async (params = {}) => {
        try {
            const { data } = await api.get('/files/public/search', { params });
            return data;
        } catch (error) {
            console.error('[filesApi.searchPublic] Failed to search public files:', error.message);
            throw error;
        }
    },

    /**
     * Поиск файлов пользователя с пагинацией
     * Endpoint: GET /api/files/user/{userId}/search
     * @param {string|number} userId - ID пользователя
     * @param {Object} params - Параметры пагинации { page, size }
     * @returns {Promise<{content: Array, page: number, size: number, totalElements: number, totalPages: number, hasNext: boolean, hasPrevious: boolean}>}
     */
    searchUserFiles: async (userId, params = {}) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/files/user/${userId}/search`, { params });
            return data;
        } catch (error) {
            console.error(`[filesApi.searchUserFiles] Failed to search files for user ${userId}:`, error.message);
            throw error;
        }
    }
};
